import * as Select from '@radix-ui/react-select'
import { Check } from '@/assets/icons'
import { Tag } from '@/components/tag/Tag'

interface SelectItemProps {
  value: string
  label: string
  disabled?: boolean
  leadingIcon?: React.ReactNode
  badge?: string
  description?: string
  children?: React.ReactNode
}

/**
 * SelectItem Component
 *
 * A single selectable item in the SelectDropdown component.
 * Displays a label with optional badge and description, with a checkmark indicator when selected.
 * Handles its own wrapping when description exists.
 */
export function SelectItem({ value, label, disabled = false, leadingIcon, badge, description, children }: SelectItemProps) {
  const itemElement = (
    <Select.Item
      value={value}
      disabled={disabled}
      className="relative flex items-center px-3 py-2 cursor-pointer rounded focus:outline-hidden
       /* default */
      text-brand-neutral-700 data-[disabled]:text-brand-neutral-300 data-[disabled]:pointer-events-none data-[disabled]:cursor-default

      /* hover/focus */
      hover:bg-surface-hover hover:text-brand-500
      focus:bg-surface-hover focus:text-brand-500

      /* active - when the item is clicked */
      active:bg-surface-hover-strong active:text-brand-500
      "
    >
      {leadingIcon && (
        <span className="mr-3 inline-flex shrink-0" aria-hidden>
          {leadingIcon}
        </span>
      )}
      {/* Label and badge */}
      <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
        <Select.ItemText asChild>
          <span className="truncate select-none text-sm font-medium">{children || label}</span>
        </Select.ItemText>
        {badge && <Tag>{badge}</Tag>}
      </div>

      {/* Checkmark shown when item is selected */}
      <div className="w-6 flex items-center justify-center shrink-0 ml-3">
        <Select.ItemIndicator>
          <Check className="h-4 w-4" />
        </Select.ItemIndicator>
      </div>
    </Select.Item>
  )

  // Render description below item if it exists
  if (description) {
    return (
      <div className="w-full">
        {itemElement}
        <div className="flex w-full px-2 mt-2 mb-2">
          <div className="flex px-3 py-3 items-center gap-3 flex-1 rounded-lg border border-border-default bg-surface-content text-xs text-brand-neutral-500">
            {description}
          </div>
        </div>
      </div>
    )
  }

  return itemElement
}
